// src/components/reports/RisksTab.jsx
import React, { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import {
  AlertTriangle, CheckCircle2, Target, Calendar, TrendingUp,
  Shield, DollarSign, ShieldAlert, Lock,
} from "lucide-react";
import { severityStyles, formatIndustryLabel } from "../../utils/reportHelpers";
import { INDUSTRY_WARNINGS } from "../../data/governanceData";

/* ─────────────────────────────────────────────────────────────────────────────
   DOLLAR QUANTIFICATION — estimate financial exposure per risk severity
   Uses industry benchmarks: avg SMB revenue $500K-2M, median $800K
   ───────────────────────────────────────────────────────────────────────────── */
const COST_ESTIMATES = {
  HIGH:   { min: 15000, max: 75000, label: '$15K – $75K', color: 'text-red-400' },
  MEDIUM: { min: 5000,  max: 25000, label: '$5K – $25K',  color: 'text-amber-400' },
  LOW:    { min: 1000,  max: 8000,  label: '$1K – $8K',   color: 'text-blue-400' },
};

const IMPACT_MULTIPLIERS = {
  financial:    1.5,
  operational:  1.2,
  reputational: 1.3,
  compliance:   2.0,
  technical:    1.0,
};

const estimateCost = (severity, impactArea) => {
  const base = COST_ESTIMATES[severity] || COST_ESTIMATES.MEDIUM;
  const mult = IMPACT_MULTIPLIERS[(impactArea || '').toLowerCase()] || 1.0;
  return {
    min: Math.round(base.min * mult),
    max: Math.round(base.max * mult),
    label: `$${Math.round(base.min * mult / 1000)}K – $${Math.round(base.max * mult / 1000)}K`,
    color: base.color,
  };
};

// Impact area icon mapping
const impactAreaIcons = {
  financial: TrendingUp,
  operational: Target,
  reputational: Shield,
  compliance: ShieldAlert,
  technical: Calendar,
  default: AlertTriangle,
};

/* ─────────────────────────────────────────────────────────────────────────────
   INDUSTRY COMPLIANCE — normalize industry key to find warnings
   ───────────────────────────────────────────────────────────────────────────── */
const normalizeForCompliance = (industry) => {
  if (!industry) return null;
  const k = industry.toLowerCase().replace(/[\s&-]+/g, '_').replace(/[^a-z_]/g, '');
  const aliases = {
    restaurant: 'hospitality', food_beverage: 'hospitality', food: 'hospitality',
    dental: 'healthcare', medical: 'healthcare', clinic: 'healthcare',
    law: 'legal', attorney: 'legal', legal: 'professional_services',
    accounting: 'finance', insurance: 'finance',
    saas: 'technology', software: 'technology',
    online_retail: 'ecommerce', e_commerce: 'ecommerce',
  };
  // Direct match first
  if (INDUSTRY_WARNINGS[k]) return k;
  // Alias match
  const aliased = aliases[k];
  if (aliased && INDUSTRY_WARNINGS[aliased]) return aliased;
  return null;
};

export const RisksTab = ({ risks, phases, tools, currentPhase = 1, industry, subVertical }) => {
  const { reportId } = useParams();
  const safeId = reportId || 'default';

  const [mitigationsDone, setMitigationsDone] = useState(() => {
    try {
      const saved = localStorage.getItem(`riskmitigations-${safeId}`);
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });

  const [filter, setFilter] = useState('All');

  const toggleMitigation = (riskKey, idx) => {
    setMitigationsDone(prev => {
      const key = `${riskKey}-${idx}`;
      const next = { ...prev, [key]: !prev[key] };
      try {
        localStorage.setItem(`riskmitigations-${safeId}`, JSON.stringify(next));
      } catch {}
      return next;
    });
  };

  const totalMitigated = (risks || []).filter(r => {
    const mits = Array.isArray(r.mitigation) ? r.mitigation : r.mitigation ? [r.mitigation] : [];
    return mits.length > 0 &&
      mits.every((_, i) => mitigationsDone[`${r.id || r.risk}-${i}`]);
  }).length;

  const highRisks = (risks || []).filter(r => r.severity === 'HIGH').length;

  // Total financial exposure estimate
  const totalExposure = useMemo(() => {
    if (!risks?.length) return null;
    let min = 0, max = 0;
    risks.forEach(r => {
      const cost = estimateCost(r.severity || 'MEDIUM', r.impactArea);
      min += cost.min;
      max += cost.max;
    });
    return { min, max };
  }, [risks]);

  // Industry compliance warning
  // R42 (Sarah R36g verdict): sub_vertical takes precedence so PM
  // operators see the FCRA/FHA tenant-applicant notice, not the
  // generic Real Estate "buyers/renters" notice that didn't apply.
  const _svKey = (subVertical || '').toLowerCase();
  const complianceKey = (_svKey && INDUSTRY_WARNINGS[_svKey])
    ? _svKey
    : normalizeForCompliance(industry);
  const complianceWarning = complianceKey ? INDUSTRY_WARNINGS[complianceKey] : null;

  if (!risks?.length) {
    return (
      <div className="text-center py-16 text-slate-400 bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-xl">
        <Shield className="w-12 h-12 mx-auto mb-4 opacity-30" />
        <p className="text-lg">No major risks flagged at this time.</p>
        <p className="text-sm mt-2">
          Your current plan has a low-risk profile — but we'll keep monitoring.
        </p>
      </div>
    );
  }

  const filteredRisks = risks.filter(r => {
    if (filter === 'All') return true;
    if (filter === 'High') return r.severity === 'HIGH';
    const mits = Array.isArray(r.mitigation) ? r.mitigation : r.mitigation ? [r.mitigation] : [];
    const allDone = mits.length > 0 && mits.every((_, i) => mitigationsDone[`${r.id || r.risk}-${i}`]);
    if (filter === 'Mitigated') return allDone;
    if (filter === 'Active Now') {
      const aff = r.affectedPhases && phases?.length
        ? phases.filter(p => r.affectedPhases.includes(p.id ?? p.phaseId))
        : [];
      return aff.length === 0 || aff.some(p => (p.phase ?? p.id) === currentPhase);
    }
    return true;
  });

  return (
    <div className="space-y-6">

      {/* ── Industry Compliance Banner (merged from GovernanceTab) ── */}
      {complianceWarning && (
        <div className="bg-amber-500/8 border border-amber-500/25 rounded-xl p-4 flex items-start gap-3">
          <Lock className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-semibold text-amber-400 mb-1">
              {formatIndustryLabel(industry, 'Industry', subVertical)} Compliance Notice
            </h4>
            <p className="text-sm text-slate-300">{complianceWarning}</p>
          </div>
        </div>
      )}

      {/* ── Summary bar with financial exposure ── */}
      <div className="space-y-4">
        <div className="bg-slate-900/70 backdrop-blur-xl border border-white/10
                        rounded-xl p-5 space-y-4">
          <div className="grid sm:grid-cols-3 gap-4">
            {/* Mitigation progress */}
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Mitigation Progress</p>
              <p className="text-2xl font-bold text-white">
                {totalMitigated}<span className="text-slate-500 text-lg">/{risks.length}</span>
              </p>
              <div className="w-full bg-white/8 rounded-full h-1.5 overflow-hidden mt-2">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-teal-400
                             rounded-full transition-all duration-500"
                  style={{ width: `${risks.length ? (totalMitigated / risks.length) * 100 : 0}%` }}
                />
              </div>
            </div>
            {/* High severity count */}
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">High Severity</p>
              <p className={`text-2xl font-bold ${highRisks > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                {highRisks}
              </p>
              <p className="text-xs text-slate-500 mt-2">
                {highRisks === 0 ? 'No critical risks — good position' : `${highRisks} risk${highRisks > 1 ? 's' : ''} need${highRisks === 1 ? 's' : ''} immediate attention`}
              </p>
            </div>
            {/* Financial exposure */}
            {totalExposure && (
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Est. Total Exposure</p>
                <p className="text-2xl font-bold text-amber-400">
                  ${Math.round(totalExposure.min / 1000)}K – ${Math.round(totalExposure.max / 1000)}K
                </p>
                <p className="text-xs text-slate-500 mt-2">
                  if unmitigated over 12 months
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Filter pills */}
        <div className="flex flex-wrap gap-2">
          {['All', 'High', 'Active Now', 'Mitigated'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors
                ${filter === f
                  ? 'bg-blue-500 border-blue-500 text-white'
                  : 'bg-slate-900/70 border-white/10 text-slate-400 hover:border-white/20'}`}
            >
              {f}{f === 'High' && highRisks > 0 ? ` (${highRisks})` : ''}
              {f === 'Mitigated' ? ` (${totalMitigated})` : ''}
            </button>
          ))}
        </div>
      </div>

      {/* ── Risk Cards ── */}
      {filteredRisks.map((risk) => {
        const severityKey = risk.severity || "MEDIUM";
        const s = severityStyles[severityKey] ?? severityStyles.MEDIUM;
        const impactKey = String(risk.impactArea || "").toLowerCase().trim();
        const ImpactIcon = impactAreaIcons[impactKey] ?? impactAreaIcons.default;
        const costEst = estimateCost(severityKey, risk.impactArea);

        // probability: 0-1
        const baseProb = risk.probability ?? (severityKey === "HIGH" ? 0.7 : severityKey === "MEDIUM" ? 0.4 : 0.2);
        const probability = Math.min(Math.max(baseProb, 0), 1);
        const probabilityPercent = Math.round(probability * 100);

        const probabilityColor = probabilityPercent >= 70 ? "bg-red-500"
          : probabilityPercent >= 40 ? "bg-amber-500" : "bg-blue-500";
        const probabilityTextColor = probabilityPercent >= 70 ? "text-red-400"
          : probabilityPercent >= 40 ? "text-amber-400" : "text-[#3b82f6]";
        const probabilityBorderColor = probabilityPercent >= 70 ? "border-red-500/30"
          : probabilityPercent >= 40 ? "border-amber-500/30" : "border-blue-500/30";

        const affectedPhases = risk.affectedPhases && phases?.length
          ? phases.filter((p) => risk.affectedPhases.includes(p.id ?? p.phaseId))
          : [];

        const relatedTools = risk.relatedTools && tools?.length
          ? tools.filter((t) => risk.relatedTools.includes(t.id ?? t.toolId))
          : [];

        const mitigations = Array.isArray(risk.mitigation)
          ? risk.mitigation
          : risk.mitigation ? [risk.mitigation] : [];

        const riskKey = risk.id || risk.risk;
        const allMitigated = mitigations.length > 0 &&
          mitigations.every((_, idx) => mitigationsDone[`${riskKey}-${idx}`]);

        const isActiveNow = affectedPhases.length === 0 ||
          affectedPhases.some(p => (p.phase ?? p.id) === currentPhase);
        const isFuture = affectedPhases.length > 0 &&
          affectedPhases.every(p => (p.phase ?? p.id) > currentPhase);

        return (
          <Card
            key={riskKey}
            className={`border-l-4 ${s.border} ${s.bg} bg-slate-900/70 backdrop-blur-xl
              transition-opacity ${allMitigated ? 'opacity-60' : ''}`}
          >
            <CardContent className="p-6">
              {/* Header */}
              <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className={`w-6 h-6 mt-0.5 ${s.icon}`} />
                  <div>
                    <h3 className="text-xl font-semibold text-white">{risk.risk}</h3>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      {risk.impactArea && (
                        <div className="flex items-center gap-1 text-sm text-slate-400">
                          <ImpactIcon className="w-4 h-4" />
                          <span className="capitalize">{risk.impactArea}</span>
                        </div>
                      )}
                      {isActiveNow && !allMitigated && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full
                                         bg-amber-500/20 text-amber-300 border border-amber-500/30">
                          Active Now
                        </span>
                      )}
                      {isFuture && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full
                                         bg-white/5 text-slate-500 border border-white/10">
                          Phase {affectedPhases[0]?.phase} — not yet
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 justify-end">
                  {allMitigated && (
                    <Badge className="bg-blue-500/20 text-[#3b82f6] text-xs border border-blue-500/30">
                      Mitigated
                    </Badge>
                  )}
                  <Badge className={`bg-slate-900/60 ${probabilityTextColor} ${probabilityBorderColor}`}>
                    {probabilityPercent}% probable
                  </Badge>
                  <Badge variant={severityKey === "HIGH" ? "destructive" : "secondary"}>
                    {severityKey}
                  </Badge>
                </div>
              </div>

              {/* Likelihood + Financial Exposure row */}
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <div className="flex justify-between text-xs text-slate-400 mb-1">
                    <span>Likelihood</span>
                    <span>{probabilityPercent}%</span>
                  </div>
                  <div className="w-full bg-white/10 rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full ${probabilityColor}`}
                      style={{ width: `${probabilityPercent}%` }}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <DollarSign className={`w-4 h-4 ${costEst.color}`} />
                  <div>
                    <span className={`text-sm font-semibold ${costEst.color}`}>{costEst.label}</span>
                    <span className="text-xs text-slate-500 ml-1.5">potential exposure</span>
                  </div>
                </div>
              </div>

              {/* Description */}
              {risk.description && (
                <p className="text-slate-300 text-sm mb-4">{risk.description}</p>
              )}

              {/* Affected phases */}
              {affectedPhases.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-[#3b82f6] mb-2 flex items-center gap-2">
                    <Target className="w-4 h-4" /> Affected Phases
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {affectedPhases.map((phase) => (
                      <Badge key={phase.id ?? phase.phase} className="bg-white/8 text-slate-300 border border-white/8">
                        Phase {phase.phase}: {phase.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Related tools */}
              {relatedTools.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-blue-400 mb-2 flex items-center gap-2">
                    <Shield className="w-4 h-4" /> Tools Most Exposed
                  </h4>
                  <div className="flex flex-wrap gap-2 text-xs">
                    {relatedTools.map((tool) => (
                      <Badge key={tool.id ?? tool.toolId ?? tool.tool_name}
                        className="bg-slate-900/60 text-slate-200 border border-white/8">
                        {tool.tool_name ?? tool.name ?? "Tool"}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Mitigation steps */}
              <div className="mt-4">
                <h4 className="text-sm font-medium text-[#3b82f6] mb-2 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" /> Mitigation Steps
                </h4>
                {mitigations.length ? (
                  <ul className="space-y-2">
                    {mitigations.map((step, idx) => (
                      <li
                        key={`${riskKey}-mitigation-${idx}`}
                        onClick={() => toggleMitigation(riskKey, idx)}
                        className="flex gap-2 text-sm cursor-pointer group"
                      >
                        <CheckCircle2 className={`w-4 h-4 mt-0.5 flex-shrink-0 transition-colors
                          ${mitigationsDone[`${riskKey}-${idx}`]
                            ? 'text-[#3b82f6]' : 'text-slate-600 group-hover:text-slate-400'}`}
                        />
                        <span className={`transition-colors
                          ${mitigationsDone[`${riskKey}-${idx}`]
                            ? 'line-through text-slate-500' : 'text-slate-300'}`}>
                          {step}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-slate-400 italic">
                    No specific mitigations listed — focus on early detection
                    and tighter monitoring for this risk.
                  </p>
                )}
              </div>

              {/* Footer context */}
              <div className="mt-4 text-xs text-slate-400 flex flex-wrap gap-3 border-t border-white/8 pt-3">
                {/* Only surface prevalence when the signal is strong (≥25%).
                    Low single-digit prevalence reads as "this is rare — ignore it"
                    which undermines the risk we're warning about. */}
                {risk.observedIn >= 50 && (
                  <span className="flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" /> Common in {Math.round(risk.observedIn)}% of similar businesses
                  </span>
                )}
                {risk.observedIn >= 25 && risk.observedIn < 50 && (
                  <span className="flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" /> Seen in roughly {Math.round(risk.observedIn)}% of similar businesses
                  </span>
                )}
                {risk.firstSeen && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> First seen: {risk.firstSeen}
                  </span>
                )}
                {(!risk.observedIn || risk.observedIn < 25) && !risk.firstSeen && (
                  <span>Common in {severityKey === "HIGH" ? "many" : "some"} similar implementations</span>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
